/**
 * **What a line chart says in words** — its accessible name, and the data behind it as rows.
 *
 * ── A picture with a name is not a picture with data ──────────────────────────────────────────
 * Every chart in this app is a `role="img"` carrying one noun: "Tonaż", "Sen", "Wyciskanie". A
 * sighted trainer reads eight weeks of shape out of it; a screen-reader user was told there is a
 * picture called Tonaż and nothing else. The vendored bklit renderer contributes nothing here —
 * it draws a series and has no opinion about naming one — so the numbers reached the eye and the
 * pointer and stopped there.
 *
 * Two things fix that and they are different jobs. The NAME carries the shape — how many points,
 * over what span, between what values, where it ends, and how many readings are missing — which is
 * what a glance gives. The ROWS carry the data, in a visually-hidden table beside the picture, which
 * is what reading the chart carefully gives. Neither substitutes for the other, and neither
 * substitutes for the keyboard path `ReadoutChart` puts on the plot itself.
 *
 * ── Why the summary is pure and takes its formatters ──────────────────────────────────────────
 * The figures have to print exactly as the axis prints them — `9,7 t`, not `9.7` — and the chart
 * already owns that formatter. Passing it in is what stops a second one appearing here and drifting;
 * it is also what makes this testable without a renderer or a locale.
 *
 * No React, no DOM, no i18next: `t` arrives as a parameter.
 */

import type { ChartPoint } from './chartSeries';

export interface ChartSummaryDeps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  /** The axis's own value formatter, so `9,7 t` reads the same in both channels. */
  readonly formatValue: (value: number) => string;
  readonly formatDate: (at: Date) => string;
}

export interface ChartSummaryRow {
  readonly key: string;
  readonly date: string;
  /** The formatted figure, or the word for a reading nobody took. */
  readonly value: string;
}

export interface ChartSummary {
  /** The chart's accessible name. Never empty — it always at least says what the chart is. */
  readonly label: string;
  readonly caption: string;
  readonly rows: readonly ChartSummaryRow[];
}

/**
 * One chart, in words and in rows.
 *
 * `name` is the caller's own noun and is never derived here — the same string the eyebrow above the
 * chart prints, so the two channels name one thing.
 */
export function chartSummary(
  points: readonly ChartPoint[],
  name: string,
  deps: ChartSummaryDeps,
): ChartSummary {
  const { t, formatValue, formatDate } = deps;
  const measured = points.flatMap((point) => (point.y === null ? [] : [point.y]));
  const first = points.at(0);
  const last = points.at(-1);

  const rows = points.map((point) => ({
    // The date, not the index: two points can share a date in no series this app draws, and an
    // index would make the key move every time a week drops off the front of the window.
    key: point.x.toISOString(),
    date: formatDate(point.x),
    value: point.y === null ? t('web:chart.noReading') : formatValue(point.y),
  }));

  const caption = t('web:chart.tableCaption', { name });

  if (measured.length === 0 || first === undefined || last === undefined) {
    return { label: t('web:chart.labelEmpty', { name }), caption, rows };
  }

  const gaps = points.length - measured.length;
  const shape = t('web:chart.label', {
    name,
    count: measured.length,
    from: formatDate(first.x),
    to: formatDate(last.x),
    min: formatValue(Math.min(...measured)),
    max: formatValue(Math.max(...measured)),
    // The LAST measured value rather than the last point's, so a series that ends on a gap still
    // says where it actually got to instead of trailing off into the word for nothing.
    latest: formatValue(measured[measured.length - 1] ?? 0),
  });

  // Announced only when there are any. "0 brakujących odczytów" on every chart that has none is
  // noise on the one channel that cannot skim.
  return {
    label: gaps === 0 ? shape : `${shape} ${t('web:chart.labelGaps', { count: gaps })}`,
    caption,
    rows,
  };
}
